import React, { useCallback, useMemo } from "react";
import Icon from "newComponents/Shared/_Atoms/Icons/Icon/Icon";
import InfoIcon from "newComponents/Shared/_Atoms/Icons/InfoIcon/InfoIcon";
import colors from "constants/colors";
import { genClasses } from "utils/textHelpers";
import { useRouter } from "next/router";
import INFOS from "constants/localization/infos";

interface IProps {
  isLogged?: boolean;
  avatarURL: string;
  name?: string;
  tooltipInfo?: string;
  linkTarget?: string;
  showEditIcon?: boolean;
  /* List size avatar, the big one is default, large is for profile pages */
  small?: boolean;
  smallest?: boolean;
  large?: boolean;
  inactive?: boolean;
  type?: "student" | "instructor" | "classroom" | "location";
  className?: string;
  archived?: boolean;
}

const ICON_BY_TYPE = {
  student: "students",
  instructor: "teacher",
  classroom: "users",
  location: "map-marker"
};

export const Avatar: React.FC<IProps> = ({
  avatarURL,
  isLogged = true,
  name,
  tooltipInfo,
  linkTarget,
  showEditIcon,
  small,
  smallest,
  large,
  inactive,
  type = "student",
  className,
  archived
}) => {
  const router = useRouter();
  const getInitialsAvatar = useCallback(() => {
    const nameParts = name
      .replace(/[^A-Z\d`.?-]+/gi, "_")
      .split("_")
      .filter((e) => e !== "undefined")
      .filter((e) => e);
    const initials =
      nameParts.length > 0 ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}` : "";

    return nameParts.length > 0 ? (
      <div
        className={genClasses("generated-avatar", {
          small: small,
          smallest: smallest,
          large: large,
          inactive: inactive,
          archived: archived
        })}
        style={{
          width: "100px",
          height: "100px",
          ...(!inactive
            ? {
                backgroundColor: type === "student" ? colors.studentBlue : colors.instructorGreen
              }
            : {})
        }}
      >
        {initials}
      </div>
    ) : (
      <Icon
        icon={ICON_BY_TYPE[type] as IconType}
        color={colors.grey}
        className={genClasses("avatar-icon", {
          small: small,
          smallest: smallest,
          large: large,
          inactive: inactive
        })}
        style={{
          width: "100px",
          height: "100px"
        }}
      />
    );
  }, [name, type, inactive]);

  const renderAvatar = useMemo(() => {
    return avatarURL ? (
      <img
        className={genClasses(`avatar-image`, {
          small: small,
          smallest: smallest,
          large: large,
          inactive: inactive,
          archived: archived
        })}
        src={avatarURL}
        alt="profile-avatar"
      />
    ) : name ? (
      getInitialsAvatar()
    ) : (
      <Icon
        icon={ICON_BY_TYPE[type] as IconType}
        color={colors.grey}
        className={genClasses(`avatar-icon`, {
          small: small,
          smallest: smallest,
          large: large
        })}
      />
    );
  }, [avatarURL, name, type, inactive]);

  const handleLink = () => router.push(linkTarget);

  const getToolTipInfo = () => {
    if (inactive) {
      switch (type) {
        case "student":
          return INFOS.inactiveStudentTooltip;
        default:
          return INFOS.generalInactiveTooltip(type);
      }
    } else return null;
  };
  return (
    <div className={genClasses("avatar", { inactive: inactive, className })}>
      {isLogged && (
        <div
          className={genClasses("avatar-wrapper-image", { clickable: Boolean(linkTarget) })}
          onClick={linkTarget ? () => handleLink() : null}
        >
          {renderAvatar}
          {showEditIcon && (
            <div className="avatar-button-edit">
              <Icon icon="edit" className="avatar-button-edit-icon" color={colors.grey} />
            </div>
          )}
          {inactive && (
            <div className={genClasses("avatar-tooltip", { small: small })}>
              <InfoIcon
                info={tooltipInfo || getToolTipInfo()}
                toolTipStyle={{ width: 300 }}
                color={colors.grey}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
